import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { FiGift, FiMessageSquare, FiShoppingCart, FiBarChart2, FiFileText, FiCompass, FiUser, FiHelpCircle, FiUsers, FiUserPlus, FiSettings } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import CampaignDetailsPage from "../pages/CampaignDetailsPage";
import MintSpaceSupportPage from "../pages/MintSpaceSupportPage";
import MintSpaceSettingsPage from "../pages/MintSpaceSettingsPage";
import MintSpaceAppStorePage from "../pages/MintSpaceAppStorePage";
import MintSpaceAnalyticsPage from "../pages/MintSpaceAnalyticsPage";
import MintSpaceSubmissionsPage from "../pages/MintSpaceSubmissionsPage";
import MintSpaceAccountsPage from "../pages/MintSpaceAccountsPage";
import MintSpaceMembersPage from "../pages/MintSpaceMembersPage";
import MintSpaceRequestsPage from "../pages/MintSpaceRequestsPage";
import HomePage from "../pages/HomePage";
import { getMintSpaceById, getSupportThreads } from "../api/mintSpace";
import { getCampaign } from "../api/campaigns";
import { useImageLoader } from "../hooks/useImageLoader";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import MintSpaceShell from "./MintSpaceShell";
import MintSpaceSidebarBannerHeader from "./MintSpaceSidebarBannerHeader";
import MintSpaceRatingStars from "./MintSpaceRatingStars";
import {
  billingMintTopNavClass,
  billingMintNavPillClass,
} from "../styles/mintlyBillingChrome";
import { mintSpaceDiscoverTopNavClass } from "../styles/feedSurface";
import MintSpaceNavButton from "./motion/MintSpaceNavButton";

const CampaignDetailsWindow = ({ isDark, setToast }) => {
  const { id, spaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const { t } = useTranslation();
  
  // URL ?nav= wins; otherwise default to Creator Rewards (ignore stale localStorage so Discover → campaign opens campaigns)
  const getInitialNav = () => searchParams.get("nav") || "campaigns";
  
  const [activeNav, setActiveNav] = useState(getInitialNav());
  const hasInitializedNav = useRef(false);
  const isInitialLoad = useRef(true);
  
  // Sync ?nav= to URL when activeNav changes (after first paint)
  useEffect(() => {
    if (hasInitializedNav.current && activeNav && (id || spaceId)) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        const currentNav = newParams.get("nav");
        // Only update if nav actually changed to avoid loops
        if (currentNav !== activeNav) {
          newParams.set("nav", activeNav);
          return newParams;
        }
        return prev;
      }, { replace: true });
    } else {
      hasInitializedNav.current = true;
    }
  }, [activeNav, id, spaceId, setSearchParams]);
  const [mintSpace, setMintSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headerImagePreview, setHeaderImagePreview] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const containerRef = useRef(null);
  const { imagesLoaded } = useImageLoader(containerRef);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";

  // Fetch mint space data
  useEffect(() => {
    const fetchMintSpace = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      // Reset state when id or spaceId changes
      setMintSpace(null);
      setIsOwner(false);
      setIsModerator(false);
      setIsMember(false);
      setHeaderImagePreview(null);
      hasInitializedNav.current = false; // Reset initialization flag when id/spaceId changes
      isInitialLoad.current = true; // Reset initial load flag
      
      try {
        setLoading(true);
        let mintSpaceId = spaceId;
        
        // If no spaceId in URL, try to get it from campaign data
        if (!mintSpaceId) {
          const campaignResult = await getCampaign(id);
          if (campaignResult.success && campaignResult.data?.mintSpace) {
            const mintSpaceData = campaignResult.data.mintSpace;
            mintSpaceId = typeof mintSpaceData === 'object' ? mintSpaceData._id : mintSpaceData;
          }
        }
        
        // Fetch mint space if we have an ID
        if (mintSpaceId) {
          const result = await getMintSpaceById(mintSpaceId);
          if (result.success && result.data) {
            setMintSpace(result.data);
            setIsOwner(result.data.isOwner || false);
            setIsModerator(result.data.isModerator || false);
            setIsMember(result.data.isMember || false);
            if (result.data.logo) {
              setHeaderImagePreview(result.data.logo);
            }
            if (isInitialLoad.current) {
              const navParam = searchParams.get("nav");
              if (!navParam) {
                if (result.data.installedApps?.includes("creator-rewards")) {
                  setActiveNav("campaigns");
                } else if (result.data.isOwner) {
                  setActiveNav("app-store");
                } else {
                  setActiveNav("members");
                }
              }
              isInitialLoad.current = false;
            }
          }
        }
      } catch (error) {
        console.error("Error loading Mint Space:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMintSpace();
  }, [id, spaceId, setToast]); // Removed searchParams to prevent reloads on navigation

  useEffect(() => {
    if (!mintSpace?._id || !mintSpace.pages?.support?.enabled) return;
    if (!isOwner && !isModerator) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getSupportThreads(mintSpace._id);
        if (
          !cancelled &&
          res.success &&
          res.data &&
          typeof res.data.totalUnread === "number"
        ) {
          setSupportUnreadCount(res.data.totalUnread);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpace?._id, mintSpace?.pages?.support?.enabled, isOwner, isModerator]);

  useEffect(() => {
    if (!socket || !mintSpace?._id || !mintSpace.pages?.support?.enabled) return;
    if (!isOwner && !isModerator) return;
    socket.emit("join_mintspace_staff", { mintSpaceId: mintSpace._id });
    const onBump = () => {
      getSupportThreads(mintSpace._id)
        .then((res) => {
          if (res.success && res.data && typeof res.data.totalUnread === "number") {
            setSupportUnreadCount(res.data.totalUnread);
          }
        })
        .catch(() => {});
    };
    socket.on("support_new_message", onBump);
    return () => {
      socket.off("support_new_message", onBump);
    };
  }, [socket, mintSpace?._id, mintSpace?.pages?.support?.enabled, isOwner, isModerator]);

  // Build navigation items dynamically based on installed apps
  const regularNavItems = [
    ...(mintSpace?.installedApps?.includes("creator-rewards")
      ? [{ id: "campaigns", label: t("navigation.creatorRewards"), icon: FiGift }]
      : []),
    { id: "members", label: t("navigation.members") || "Members", icon: FiUsers },
    ...(mintSpace?.pages?.support?.enabled && isMember && !isOwner && !isModerator
      ? [{ id: "support", label: t("navigation.supportChat") || "Support Chat", icon: FiMessageSquare }]
      : []),
  ];

  const adminNavItems = isOwner
    ? [
        { id: "settings", label: t("mintSpaceSettings.brandDashboard") || "Dashboard", icon: FiSettings },
        { id: "requests", label: t("navigation.requests") || "Requests", icon: FiUserPlus },
        ...(mintSpace?.pages?.support?.enabled
          ? [
              {
                id: "support",
                label: t("navigation.supportChat") || "Support Chat",
                icon: FiMessageSquare,
                badge: supportUnreadCount,
              },
            ]
          : []),
        { id: "app-store", label: t("navigation.appStore") || "App Store", icon: FiShoppingCart },
      ]
    : isModerator && mintSpace?.pages?.support?.enabled
      ? [
          {
            id: "support",
            label: t("navigation.supportChat") || "Support Chat",
            icon: FiMessageSquare,
            badge: supportUnreadCount,
          },
        ]
      : [];

  const handleMintSpaceUpdate = (updatedSpace) => {
    setMintSpace(updatedSpace);
    setIsOwner((prev) => (typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : prev));
    setIsModerator((prev) => (typeof updatedSpace.isModerator === "boolean" ? updatedSpace.isModerator : prev));
    setIsMember((prev) => (typeof updatedSpace.isMember === "boolean" ? updatedSpace.isMember : prev));
    if (updatedSpace.logo) {
      setHeaderImagePreview(updatedSpace.logo);
    }
    
    // If currently viewing Creator Rewards and it's uninstalled, switch to appropriate page
    if (activeNav === "campaigns" && !updatedSpace.installedApps?.includes("creator-rewards")) {
      const ownerNow =
        typeof updatedSpace.isOwner === "boolean" ? updatedSpace.isOwner : isOwner;
      if (ownerNow) {
        setActiveNav("app-store");
      } else {
        setActiveNav("members");
      }
    }
    if (activeNav === "support" && !updatedSpace.pages?.support?.enabled) {
      setActiveNav("settings");
    }
  };

  useEffect(() => {
    if (!mintSpace || activeNav !== "support") return;
    if (mintSpace.pages?.support?.enabled) return;
    if (isOwner) setActiveNav("settings");
    else setActiveNav("members");
  }, [mintSpace, activeNav, isOwner]);

  // Only show loading screen on initial load, not when navigating
  if (loading && !mintSpace) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-default border-r-transparent mb-4"></div>
          <div className={`text-lg ${mutedText}`}>Loading...</div>
        </div>
      </div>
    );
  }

  const resolvedMintSpaceId =
    spaceId ||
    (typeof mintSpace?._id === "string" ? mintSpace._id : mintSpace?._id?.toString?.());

  // Standalone /campaigns/:id links → unified mint space window
  if (!loading && id && resolvedMintSpaceId && !spaceId) {
    return (
      <Navigate to={`/mint-space/${resolvedMintSpaceId}/campaigns/${id}`} replace />
    );
  }

  // Get connected accounts count
  const getConnectedAccountsCount = () => {
    if (!user) return 0;
    // Handle both user.socials and user.profile.socials structures
    const socials = user.socials || user.profile?.socials || {};
    let count = 0;
    if (socials.instagram && socials.instagram.trim() !== "") count++;
    if (socials.youtube && socials.youtube.trim() !== "") count++;
    if (socials.tiktok && socials.tiktok.trim() !== "") count++;
    if (socials.facebook && socials.facebook.trim() !== "") count++;
    return count;
  };

  // Build navbar items
  const navbarItems = [
    { id: "analytics", label: t("navigation.analytics") || "Analytics", icon: FiBarChart2 },
    { id: "submissions", label: t("navigation.submissions") || "Submissions", icon: FiFileText },
    { id: "discover", label: t("navigation.discover") || "Discover", icon: FiCompass },
    {
      id: "accounts",
      label: `${t("navigation.accounts") || "Accounts"} (${getConnectedAccountsCount()})`,
      icon: FiUser,
    },
  ];

  const sidebarHeader = (
    <MintSpaceSidebarBannerHeader
      isDark={isDark}
      mutedText={mutedText}
      title={mintSpace?.name || "Campaign"}
      imageUrl={headerImagePreview}
      imageAlt={mintSpace?.name}
      placeholderLabel={mintSpace?.name ? "No Image" : undefined}
      onBack={() => {
        const mintSpaceId = spaceId || mintSpace?._id;
        if (!mintSpaceId) {
          navigate("/");
          return;
        }
        // Pop one entry so Back matches browser history (avoid pushing /mint-space/:id which breaks Back → Discover).
        if (typeof window !== "undefined" && window.history.length > 1) {
          navigate(-1);
        } else {
          navigate(`/mint-space/${mintSpaceId}?nav=campaigns&campaign=${id}`);
        }
      }}
      backAriaLabel={t("common.back") || "Back"}
    >
      {mintSpace?._id ? (
        <MintSpaceRatingStars
          mintSpaceId={mintSpace._id}
          isDark={isDark}
          isOwner={isOwner}
          isMember={isMember}
          compact
        />
      ) : null}
    </MintSpaceSidebarBannerHeader>
  );

  const sidebarNav = (
    <>
      {adminNavItems.length > 0 && (
        <>
          <div className="mb-1.5 px-2">
            <h4 className={`text-caption-1-semibold uppercase tracking-[0.12em] ${mutedText}`}>
              {t("windows.adminSettings")}
            </h4>
          </div>
          <ul className="mb-2.5 space-y-0.5">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeNav === item.id;
              return (
                <li key={item.id}>
                  <MintSpaceNavButton
                    isActive={isActive}
                    isDark={isDark}
                    layoutGroup="campaign-sidebar"
                    onClick={() => setActiveNav(item.id)}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2} />
                    <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="flex h-4 min-w-[1.1rem] shrink-0 items-center justify-center rounded-md bg-rose-500 px-1 text-caption-1-semibold text-white">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </MintSpaceNavButton>
                </li>
              );
            })}
          </ul>
          <div className={`mb-2.5 border-t border-separator-default`} />
        </>
      )}
      <ul className="space-y-0.5">
        {regularNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeNav === item.id;
          return (
            <li key={item.id}>
              <MintSpaceNavButton
                isActive={isActive}
                isDark={isDark}
                layoutGroup="campaign-sidebar"
                onClick={() => setActiveNav(item.id)}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2} />
                <span className="min-w-0 truncate text-left">{item.label}</span>
              </MintSpaceNavButton>
            </li>
          );
        })}
      </ul>
    </>
  );

  const mobileNav = [...adminNavItems, ...regularNavItems].map((item) => {
    const Icon = item.icon;
    const isActive = activeNav === item.id;
    return (
      <MintSpaceNavButton
        key={item.id}
        isActive={isActive}
        isDark={isDark}
        mobile
        layoutGroup="campaign-mobile"
        onClick={() => setActiveNav(item.id)}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left text-subheadline-semibold">{item.label}</span>
        {item.badge > 0 && (
          <span className="flex h-4 min-w-[1.1rem] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-caption-1-semibold text-white">
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </MintSpaceNavButton>
    );
  });

  const mainTop =
    activeNav === "campaigns" ||
    activeNav === "discover" ||
    activeNav === "analytics" ||
    activeNav === "submissions" ? (
      <nav
        className={`hidden md:flex items-center justify-between w-full px-3 lg:px-4 py-2 flex-shrink-0 transition-opacity duration-200 ${
          activeNav === "discover" ? mintSpaceDiscoverTopNavClass(isDark) : billingMintTopNavClass(isDark)
        }`}
      >
        <div className="flex-1 flex items-center justify-start" />
        <div className="flex items-center gap-1">
          {navbarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeNav === item.id;
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => {
                  if (item.id === "discover") {
                    setActiveNav("discover");
                  } else {
                    setActiveNav(item.id);
                  }
                }}
                className={`relative flex items-center justify-center gap-1 whitespace-nowrap transition ${billingMintNavPillClass(
                  isDark,
                  isActive
                )}`}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 ${isActive ? ("text-status-success") : ""}`}
                />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 flex items-center justify-end gap-3">
          <button
            type="button"
            className={`transition-colors hover:opacity-80 ${mutedText}`}
            aria-label="Help and Support"
            onClick={() => {
              if (mintSpace?.pages?.support?.enabled && (isOwner || isModerator)) {
                setActiveNav("support");
              } else {
                setToast(t("mintSpace.supportNotAvailable"));
              }
            }}
          >
            <FiHelpCircle className="w-5 h-5" />
          </button>
          {user && (
            <button
              onClick={() => navigate("/profile")}
              className="relative size-7 rounded-full overflow-hidden bg-accent-default cursor-pointer transition-transform hover:scale-105"
              type="button"
            >
              {getProfileAvatarUrl(user) ? (
                <img
                  src={getProfileAvatarUrl(user)}
                  alt={user.username || user.businessName || "User"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-footnote-semibold">
                  {(user.username || user.businessName || "U").charAt(0).toUpperCase()}
                </div>
              )}
            </button>
          )}
        </div>
      </nav>
    ) : null;

  const renderPanel = (navId) => {
    switch (navId) {
      case "analytics":
        return (
          <div className="p-0">
            <MintSpaceAnalyticsPage isDark={isDark} setToast={setToast} mintSpaceId={spaceId || mintSpace?._id} />
          </div>
        );
      case "submissions":
        return (
          <div className="p-0">
            <MintSpaceSubmissionsPage isDark={isDark} setToast={setToast} mintSpaceId={spaceId || mintSpace?._id} />
          </div>
        );
      case "accounts":
        return (
          <div className="p-0">
            <MintSpaceAccountsPage isDark={isDark} setToast={setToast} />
          </div>
        );
      case "campaigns":
        return (
          <div className="p-3 lg:p-4">
            <CampaignDetailsPage isDark={isDark} setToast={setToast} onNavigate={setActiveNav} />
          </div>
        );
      case "app-store":
        if (!isOwner) return null;
        return (
          <MintSpaceAppStorePage
            mintSpaceId={spaceId || mintSpace?._id}
            isDark={isDark}
            setToast={setToast}
            mintSpace={mintSpace}
            onAppChange={handleMintSpaceUpdate}
            onOpenApp={(navId) => setActiveNav(navId)}
          />
        );
      case "settings":
        if (!isOwner) return null;
        return (
          <div className="p-3 lg:p-4">
            <MintSpaceSettingsPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onUpdate={handleMintSpaceUpdate}
            />
          </div>
        );
      case "support":
        if (!mintSpace?.pages?.support?.enabled || !(isOwner || isModerator || isMember)) return null;
        return (
          <div className="p-3 lg:p-4">
            <MintSpaceSupportPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onSupportUnreadChange={setSupportUnreadCount}
              isMintSpaceOwner={isOwner}
              isMintSpaceModerator={isModerator}
            />
          </div>
        );
      case "members":
        return (
          <div className="p-3 lg:p-4">
            <MintSpaceMembersPage isDark={isDark} setToast={setToast} isOwner={isOwner} />
          </div>
        );
      case "requests":
        if (!isOwner) return null;
        return (
          <div className="p-3 lg:p-4">
            <MintSpaceRequestsPage isDark={isDark} setToast={setToast} mintSpaceId={spaceId || mintSpace?._id} />
          </div>
        );
      case "discover":
        return (
          <div className="p-3 lg:p-4">
            <HomePage
              isDark={isDark}
              audience={user?.accountType === "creator" ? "creators" : "businesses"}
              setAudience={() => {}}
              setToast={setToast}
              highlightedCampaignId={id || undefined}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <MintSpaceShell
      ref={containerRef}
      isDark={isDark}
      windowVariant={activeNav === "discover" ? "discover" : "default"}
      sidebarHeader={sidebarHeader}
      sidebarNav={sidebarNav}
      mobileNav={mobileNav}
      mainTop={mainTop}
      activePanelKey={activeNav}
      renderPanel={renderPanel}
    />
  );
};

export default CampaignDetailsWindow;

